import { Separator } from '@/components/ui/separator';
import { Routes } from '@/utils/routes';
import { PickingJobStatus } from '@/graphql/generatedTypes';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import i18n from '@/utils/i18n';
import { pickingJobColumns } from './columns';
import { PickingJobDataTable } from './PickingJobDataTable';
import { DEFAULT_STATUSES, DELIVERED_WINDOW_DAYS } from './pickingJobFilters';
import { pickingJobStatusLabel } from './pickingJobLabels';
import { PAGE_SIZE, usePickingJobList } from './usePickingJobList';

export function PickingJobListView() {
  useDocumentTitle(i18n.t('Picking:pageTitle'));

  const list = usePickingJobList();

  return (
    <PickingJobDataTable
      columns={pickingJobColumns}
      data={list.rows}
      totalCount={list.totalCount}
      currentPage={list.currentPage}
      pageSize={PAGE_SIZE}
      onPageChange={list.setCurrentPage}
      isLoading={list.loading}
      isError={list.error != null}
      sorting={list.sorting}
      onSortingChange={list.setSorting}
      columnVisibility={list.columnVisibility}
      onColumnVisibilityChange={list.setColumnVisibility}
      availableFilters={list.availableFilters}
      activeFilters={list.activeFilters}
      onFilterChange={list.handleFilterChange}
      onFilterAdd={list.handleFilterAdd}
      onFilterRemove={list.handleFilterRemove}
      search={list.search}
      onSearchChange={list.setSearch}
      filterHint={[
        list.deliveredFloorActive &&
          i18n.t('Picking:deliveredWindowHint', {
            days: DELIVERED_WINDOW_DAYS,
          }),
        list.searchSkipsNotStarted && i18n.t('Picking:searchSkipsNotStarted'),
        // Said out loud: the rows no longer match the status chip above them,
        // and an unexplained mismatch reads as a broken filter.
        list.searchIgnoresStatus && i18n.t('Picking:searchAllStatusesHint'),
      ]
        .filter(Boolean)
        .join(' ')}
      // A job that has not been started yet has no persisted row, so there is
      // nothing to open: getPickingJob cannot resolve it without its context.
      getRowHref={(row) =>
        row.status === PickingJobStatus.PickingNotStarted
          ? null
          : Routes.pickingJobDetails(row.id)
      }
      header={
        <>
          <div className="tw:flex tw:flex-col tw:gap-2">
            <h1 className="tw:text-4xl tw:font-semibold">
              {i18n.t('Picking:pageTitle')}
            </h1>
            <p className="tw:leading-6">
              {i18n.t('Picking:pageDescription', {
                statuses: DEFAULT_STATUSES.map(pickingJobStatusLabel).join(
                  ', ',
                ),
              })}
            </p>
            {list.failedWarehouses.length > 0 && (
              <p className="tw:leading-6 tw:text-destructive">
                {i18n.t('Picking:partialLoadError', {
                  warehouses: list.failedWarehouses.join(', '),
                })}
              </p>
            )}
          </div>

          <Separator />
        </>
      }
    />
  );
}
